import { useState, useEffect, useMemo } from "react"
import {
  IconCheck,
  IconReceipt,
  IconCreditCard,
  IconDownload,
} from "@tabler/icons-react"
import {
  useSubscriptionPlans,
  useActiveSubscription,
  useInvoices,
  useCustomerPortal,
} from "@/data/hooks/api"
import type { SubscriptionPlanResponse, InvoiceResponse, PlanInterval } from "@/data/types/subscription.types"
import { toast } from "sonner"
import { usePaddleCheckout } from "@/hooks/usePaddleCheckout"
import { cn } from "@/lib/utils"

import { Button } from "@/components/ui/button"
import { PageContainer } from "@/components/PageContainer"
import { PageHeader } from "@/components/PageHeader"
import { DataTable, type DataTableColumn } from "@/components/ui/data-table"

// Group plans by name - each name can have monthly and yearly versions
interface PlanGroup {
  name: string
  monthly?: SubscriptionPlanResponse
  annually?: SubscriptionPlanResponse
}

export function BillingPage() {
  const { data: plansData, isLoading: plansLoading } = useSubscriptionPlans()
  const { data: activeSubscription, isLoading: activeSubLoading } = useActiveSubscription()
  const { openCheckout, isLoading: isPaddleLoading } = usePaddleCheckout()
  const { refetch: fetchPortal, isFetching: isPortalLoading } = useCustomerPortal()
  // Per-plan billing interval state - defaults to annually if available, otherwise monthly
  const [planIntervals, setPlanIntervals] = useState<Record<string, PlanInterval>>({})

  // Invoice pagination state
  const [invoicePageIndex, setInvoicePageIndex] = useState(0)
  const [invoicePageSize, setInvoicePageSize] = useState(10)

  const { data: invoicesData, isLoading: invoicesLoading } = useInvoices({
    limit: invoicePageSize,
    offset: invoicePageIndex * invoicePageSize,
  })

  const plans = plansData || []
  const loading = plansLoading || activeSubLoading

  // Check if subscription is scheduled for cancellation
  const isCanceledAtPeriodEnd = activeSubscription?.cancel_at_period_end ?? false
  const periodEndDate = activeSubscription?.current_period_end

  // Group plans by name
  const planGroups = useMemo(() => {
    const groups: Record<string, PlanGroup> = {}

    plans.forEach(plan => {
      const name = plan.name.toLowerCase()
      if (!groups[name]) {
        groups[name] = { name: plan.name }
      }
      if (plan.interval === "monthly") {
        groups[name].monthly = plan
      } else if (plan.interval === "annually") {
        groups[name].annually = plan
      }
    })

    // Convert to array and sort by monthly price (or annually if no monthly)
    return Object.values(groups).sort((a, b) => {
      const priceA = a.monthly?.price ?? a.annually?.price ?? 0
      const priceB = b.monthly?.price ?? b.annually?.price ?? 0
      return priceA - priceB
    })
  }, [plans])

  // Initialize plan intervals when plans change
  useEffect(() => {
    if (planGroups.length > 0) {
      setPlanIntervals(prev => {
        const newIntervals: Record<string, PlanInterval> = { ...prev }
        let hasChanges = false
        planGroups.forEach(group => {
          const name = group.name.toLowerCase()
          if (!newIntervals[name]) {
            newIntervals[name] = group.annually ? "annually" : "monthly"
            hasChanges = true
          }
        })
        return hasChanges ? newIntervals : prev
      })
    }
  }, [planGroups])

  // Get the plan for the specific plan's billing interval
  const getPlanForInterval = (group: PlanGroup): SubscriptionPlanResponse | undefined => {
    const planName = group.name.toLowerCase()
    const interval = planIntervals[planName] || (group.annually ? "annually" : "monthly")
    return interval === "monthly" ? group.monthly : group.annually
  }

  // Toggle billing interval for a specific plan
  const togglePlanInterval = (planName: string) => {
    const name = planName.toLowerCase()
    setPlanIntervals(prev => {
      const current = prev[name] || (planGroups.find(g => g.name.toLowerCase() === name)?.annually ? "annually" : "monthly")
      return {
        ...prev,
        [name]: current === "monthly" ? "annually" : "monthly"
      }
    })
  }

  // Check if plan group has both intervals
  const hasBothIntervals = (group: PlanGroup) => {
    return group.monthly && group.annually
  }

  const formatPrice = (price: number, currency: string, isAnnual: boolean = false) => {
    // If annual, divide by 12 to show monthly equivalent
    const monthlyPrice = isAnnual ? price / 12 : price
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(monthlyPrice)
  }

  const isPlanCurrent = (group: PlanGroup) => {
    if (!activeSubscription) return false
    // Check if the plan name matches (case-insensitive)
    return group.name.toLowerCase() === activeSubscription.name.toLowerCase()
  }

  const isFreePlan = (group: PlanGroup) => {
    const plan = getPlanForInterval(group)
    return plan ? plan.price === 0 : false
  }

  // Plan tier hierarchy (lower index = lower tier)
  const PLAN_TIERS = ["developer", "starter", "professional", "enterprise"]

  const getPlanTier = (planName: string): number => {
    const tier = PLAN_TIERS.indexOf(planName.toLowerCase())
    return tier === -1 ? 0 : tier
  }

  const isUpgrade = (group: PlanGroup): boolean => {
    if (!activeSubscription) return true
    const currentTier = getPlanTier(activeSubscription.name)
    const targetTier = getPlanTier(group.name)
    return targetTier > currentTier
  }

  const handleManagePaymentMethod = async () => {
    try {
      const result = await fetchPortal()
      if (result.data?.portal_url) {
        window.open(result.data.portal_url, "_blank")
      }
    } catch (error) {
      console.error(error)
      toast.error("Unable to open payment portal", {
        description: "Payment method management is not available yet.",
      })
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "—"
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(dateString))
  }

  const formatInvoiceDate = formatDate

  const formatInvoiceAmount = (total: string | null, currency: string) => {
    if (!total) return "—"
    const amount = parseFloat(total)
    if (isNaN(amount)) return "—"
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    }).format(amount / 100)
  }

  const formatBillingPeriod = (billingPeriod: InvoiceResponse["billing_period"]) => {
    if (!billingPeriod) return "—"
    const start = formatDate(billingPeriod.start)
    const end = formatDate(billingPeriod.end)
    return `${start} - ${end}`
  }

  const getInvoiceStatusColor = (status: InvoiceResponse["status"]) => {
    switch (status) {
      case "completed":
        return "text-green-600 bg-green-50"
      case "pending":
        return "text-amber-600 bg-amber-50"
      case "failed":
        return "text-red-600 bg-red-50"
      default:
        return "text-[#8A8F98] bg-[#F7F8F8]"
    }
  }

  // Column definitions for invoices table
  const invoicesTableColumns: DataTableColumn<InvoiceResponse>[] = useMemo(() => [
    {
      id: "invoice_number",
      header: "Invoice",
      flex: 1.2,
      cell: ({ row }) => (
        <span className="text-[13px] text-[#08090A] font-medium">
          {row.original.invoice_number || row.original.id.slice(0, 12)}
        </span>
      ),
    },
    {
      id: "created_at",
      header: "Date",
      flex: 1,
      cell: ({ row }) => (
        <span className="text-[13px] text-[#8A8F98]">
          {formatInvoiceDate(row.original.created_at)}
        </span>
      ),
    },
    {
      id: "billing_period",
      header: "Billing Period",
      flex: 1.5,
      cell: ({ row }) => (
        <span className="text-[13px] text-[#8A8F98]">
          {formatBillingPeriod(row.original.billing_period)}
        </span>
      ),
    },
    {
      id: "status",
      header: "Status",
      flex: 0.9,
      cell: ({ row }) => (
        <span className={`inline-flex items-center px-[8px] py-[2px] rounded-[4px] text-[11px] font-medium capitalize ${getInvoiceStatusColor(row.original.status)}`}>
          {row.original.status}
        </span>
      ),
    },
    {
      id: "total",
      header: "Amount",
      flex: 1,
      cell: ({ row }) => (
        <span className="text-[13px] text-[#08090A] font-medium tabular-nums">
          {formatInvoiceAmount(row.original.total, row.original.currency)}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      flex: 0.3,
      enableSorting: false,
      align: "right",
      cell: ({ row }) => (
        row.original.invoice_url ? (
          <a
            href={row.original.invoice_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center h-[28px] w-[28px] rounded-[4px] text-[#8A8F98] hover:text-[#08090A] hover:bg-[#F7F8F8] transition-colors"
            title="Download invoice"
            onClick={(e) => e.stopPropagation()}
          >
            <IconDownload className="size-4" />
          </a>
        ) : null
      ),
    },
  ], [])

  const isPaidPlan = activeSubscription && !activeSubscription.is_free_plan

  return (
    <PageContainer maxWidth='1100px'>
      <PageHeader
        title="Billing"
        description={
          activeSubscription ? (
            <>
              You are on the <span className="font-medium text-[#08090A]">{activeSubscription.name}</span>
              {isCanceledAtPeriodEnd && periodEndDate && (
                <span className="text-red-600"> (cancels on {formatDate(periodEndDate)})</span>
              )}
              . If you have any questions or would like further support with your plan, {" "}
              <a href="mailto:support@example.com" className="text-[#08090A] underline hover:no-underline">
                contact us
              </a>
            </>
          ) : (
            "For questions about billing, contact us"
          )
        }
        rightElement={
          <div className="flex items-center gap-[8px]">
            {isPaidPlan && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleManagePaymentMethod}
                disabled={isPortalLoading}
              >
                <IconCreditCard className="size-3.5" />
                Manage subscription
              </Button>
            )}
          </div>
        }
      />

      {/* Plans Comparison */}
      {!loading && planGroups.length > 0 && (
        <div>
          {/* Plan Headers */}
          <div className="grid gap-[16px]" style={{ gridTemplateColumns: `repeat(${planGroups.length + 1}, 1fr)` }}>
            {/* Plan columns headers */}
            {planGroups.map((group) => {
              const plan = getPlanForInterval(group)
              const showToggle = hasBothIntervals(group) && !isFreePlan(group)
              const planName = group.name.toLowerCase()
              const currentInterval = planIntervals[planName] || (group.annually ? "annually" : "monthly")
              const isAnnual = currentInterval === "annually"

              return (
                <div
                  key={group.name}
                  className={cn(
                    "rounded-t-[8px] p-[20px] pb-[16px] border border-b-0",
                    isFreePlan(group) 
                      ? "border-[#EDEEF3]" 
                      : "border-[#654EDA]/30 bg-gradient-to-b from-[#654EDA]/8 to-transparent"
                  )}
                >
                  <div className="space-y-[4px]">
                    <h3 className="text-[16px] font-medium text-[#08090A]">
                      {group.name}
                    </h3>
                    <div className="text-[16px] text-[#08090A]">
                      {plan ? (
                        <>
                          <span className="font-medium">{formatPrice(plan.price, plan.currency, isAnnual)}</span>
                          <span className="text-[13px] text-[#8A8F98]">/month</span>
                        </>
                      ) : (
                        <span className="text-[#8A8F98]">—</span>
                      )}
                    </div>
                  </div>

                  {/* Billing Toggle */}
                  {showToggle && (
                    <div className="flex items-center gap-[8px] mt-[12px]">
                      <button
                        onClick={() => togglePlanInterval(group.name)}
                        className={cn(
                          "relative w-[36px] h-[20px] rounded-full transition-colors",
                          currentInterval === "annually" ? "bg-[#654EDA]" : "bg-[#D1D5DB]"
                        )}
                      >
                        <span
                          className={cn(
                            "absolute top-[2px] w-[16px] h-[16px] bg-white rounded-full transition-transform",
                            currentInterval === "annually" ? "left-[18px]" : "left-[2px]"
                          )}
                        />
                      </button>
                      <span className="text-[12px] text-[#8A8F98]">Billed yearly</span>
                    </div>
                  )}

                  {/* Description for Free */}
                  {isFreePlan(group) && (
                    <p className="text-[12px] text-[#8A8F98] mt-[12px]">Free for everyone</p>
                  )}

                  {/* Action Button */}
                  <div className="mt-[16px]">
                    {isPlanCurrent(group) ? (
                      <Button
                        variant="transparent-outline"
                        size="sm"
                        disabled
                        className="w-full h-[32px] text-[13px] font-medium"
                      >
                        Current plan
                      </Button>
                    ) : isUpgrade(group) ? (
                      <Button
                        size="sm"
                        className="w-full h-[32px] bg-[#654EDA] hover:bg-[#654EDA]/90 text-white text-[13px] font-medium"
                        onClick={() => {
                          const plan = getPlanForInterval(group)
                          if (!plan || isFreePlan(group)) return
                          if (!plan.external_id) {
                            toast.error("Plan configuration error", {
                              description: "This plan is not available for checkout. Please contact support.",
                            })
                            return
                          }
                          openCheckout(plan.external_id)
                        }}
                        disabled={isPaddleLoading}
                      >
                        Upgrade
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full h-[32px] text-[13px] font-medium"
                        onClick={() => {
                          const plan = getPlanForInterval(group)
                          if (!plan) return
                          if (isFreePlan(group)) {
                            // For free plan downgrade, might need different handling
                            toast.info("Downgrade to free plan", {
                              description: "Please contact support to downgrade to the free plan.",
                            })
                            return
                          }
                          if (!plan.external_id) {
                            toast.error("Plan configuration error", {
                              description: "This plan is not available for checkout. Please contact support.",
                            })
                            return
                          }
                          openCheckout(plan.external_id)
                        }}
                        disabled={isPaddleLoading}
                      >
                        Downgrade
                      </Button>
                    )}
                  </div>
                </div>
              )
            })}

            {/* Static Enterprise Plan Column */}
            <div className="rounded-t-[8px] p-[20px] pb-[16px] border border-b-0 border-[#654EDA]/30 bg-gradient-to-b from-[#654EDA]/8 to-transparent">
              <div className="space-y-[4px]">
                <h3 className="text-[16px] font-medium text-[#08090A]">
                  Enterprise
                </h3>
                <div className="text-[13px] text-[#08090A]">
                  <span className="text-[#8A8F98]">Custom pricing</span>
                </div>
              </div>

              {/* Billed yearly text */}
              <p className="text-[12px] text-[#8A8F98] mt-[12px]">Billed yearly</p>

              {/* Contact us Button */}
              <div className="mt-[16px]">
                <Button
                  variant="transparent-outline"
                  size="sm"
                  className="w-full max-w-none h-[32px] text-[13px] font-medium"
                  onClick={() => {
                    window.location.href = "mailto:support@example.com?subject=Enterprise Plan Inquiry"
                  }}
                >
                  Contact us
                </Button>
              </div>
            </div>
          </div>

          {/* Features Section */}
          <div
            className="grid gap-[16px]"
            style={{ gridTemplateColumns: `repeat(${planGroups.length + 1}, 1fr)` }}
          >
            {/* Plan entitlements */}
            {planGroups.map((group) => {
              const plan = getPlanForInterval(group) || group.monthly || group.annually
              if (!plan) {
                return <div key={group.name} />
              }

              return (
                <div 
                  key={group.name} 
                  className={cn(
                    "space-y-[8px] p-[20px] pt-[16px] border border-t-0 rounded-b-[8px]",
                    isFreePlan(group) ? "border-[#EDEEF3]" : "border-[#654EDA]/30"
                  )}
                >
                  {plan.entitlements.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-[8px]">
                      <IconCheck className="size-3.5 text-[#654EDA] shrink-0" />
                      <span className="text-[13px] text-[#08090A]">{feature}</span>
                    </div>
                  ))}
                </div>
              )
            })}

            {/* Enterprise Column */}
            <div className="space-y-[8px] p-[20px] pt-[16px] border border-t-0 border-[#654EDA]/30 rounded-b-[8px]">
              <div className="flex items-center gap-[8px]">
                <IconCheck className="size-3.5 text-[#654EDA] shrink-0" />
                <span className="text-[13px] text-[#08090A]">Everything in Pro</span>
              </div>
              <div className="flex items-center gap-[8px]">
                <IconCheck className="size-3.5 text-[#654EDA] shrink-0" />
                <span className="text-[13px] text-[#08090A]">Unlimited data retention</span>
              </div>
              <div className="flex items-center gap-[8px]">
                <IconCheck className="size-3.5 text-[#654EDA] shrink-0" />
                <span className="text-[13px] text-[#08090A]">SSO & SAML</span>
              </div>
              <div className="flex items-center gap-[8px]">
                <IconCheck className="size-3.5 text-[#654EDA] shrink-0" />
                <span className="text-[13px] text-[#08090A]">Dedicated support</span>
              </div>
              <div className="flex items-center gap-[8px]">
                <IconCheck className="size-3.5 text-[#654EDA] shrink-0" />
                <span className="text-[13px] text-[#08090A]">Custom integrations</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State for Plans */}
      {loading && (
        <div className="flex items-center justify-center py-[64px]">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-solid border-[#654EDA] border-r-transparent" />
        </div>
      )}

      {/* Recent Invoices */}
      <div className="space-y-[16px]">
      <h2 className="text-[18px] font-medium text-[#08090A] px-[4px]">Recent invoices</h2>
      <div className="border border-[#EDEEF3] rounded-[8px] overflow-hidden">
          <DataTable
            data={invoicesData?.items || []}
            columns={invoicesTableColumns}
            getRowId={(row) => row.id}
            pagination={{
              enabled: true,
              mode: "server",
              pageIndex: invoicePageIndex,
              pageSize: invoicePageSize,
              totalItems: invoicesData?.total ?? 0,
              pageSizeOptions: [10, 25, 50, 100],
              onPageChange: setInvoicePageIndex,
              onPageSizeChange: (newSize) => {
                setInvoicePageSize(newSize)
                setInvoicePageIndex(0)
              },
            }}
            emptyMessage="No invoices yet"
            emptyIcon={<IconReceipt className="size-6 text-[#8A8F98]" />}
            loading={invoicesLoading}
          />
        </div>
      </div>

    </PageContainer>
  )
}
